import React, { useState } from "react";
import {
  DeviceDetails,
  downloadZip,
  FileItem,
  followDownloadLink,
  getFileUrl,
  SessionFilesInfo,
} from "./adminApi";
import { nyDate, nyTime } from "./adminShell";
import CsvGraph from "./CsvGraph";
import styles from "./SessionFiles.module.css";

/** One-line file status for a session row. */
export function filesSummary(files: SessionFilesInfo): string {
  if (!files.linked)
    return files.noneReceived ? "No files received" : "not linked";
  if (files.expected == null)
    return `${files.present} ${files.present === 1 ? "file" : "files"}`;
  const missing = files.expected - files.present;
  return missing > 0
    ? `${files.present} of ${files.expected} · ${missing} missing`
    : `${files.present} of ${files.expected}`;
}

function label(item: FileItem): string {
  if (item.type === "baseline") return "Baseline";
  if (item.type === "aggregate") return "Aggregate";
  if (item.type === "rep") return `Rep ${item.repIndex ?? "?"}`;
  return item.type || "File";
}

function zipName(exercise: string | null, timestamp: string): string {
  const safe =
    (exercise || "session")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "session";
  const time = new Date(timestamp);
  const hhmm = Number.isNaN(time.getTime())
    ? "unknown-time"
    : `${nyDate(time)}_${new Intl.DateTimeFormat("en-GB", {
        timeZone: "America/New_York",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
        .format(time)
        .replace(":", "")}`;
  return `${safe}_${hhmm}.zip`;
}

/** "samsung SM-S931U · Android 16 · 11 GB RAM · app 2.2.2"; "unknown" values are left out. */
export function phoneLine(
  device: DeviceDetails | null | undefined,
): string | null {
  if (!device) return null;
  const known = (v: string | null | undefined) =>
    v && v.toLowerCase() !== "unknown" ? v : null;
  const parts = [
    [known(device.manufacturer), known(device.modelName)]
      .filter(Boolean)
      .join(" "),
    [known(device.osName), known(device.osVersion)].filter(Boolean).join(" "),
    device.totalMemory
      ? `${Math.round(device.totalMemory / 1024 ** 3)} GB RAM`
      : "",
    known(device.appVersion) ? `app ${device.appVersion}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

interface Props {
  files: SessionFilesInfo;
  device?: DeviceDetails | null;
  sessionId: string;
  email: string;
  exercise: string | null;
  timestamp: string;
  expanded?: boolean;
}

/** A session's linked files: status, per-file graph and CSV download, and a zip of everything. */
export default function SessionFiles({
  files,
  device,
  sessionId,
  email,
  exercise,
  timestamp,
  expanded = false,
}: Props) {
  const [openGraph, setOpenGraph] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!expanded) return null;
  const phone = phoneLine(device);
  const phoneNote = (
    <p className={styles.note}>
      {phone
        ? `Phone: ${phone}`
        : "Phone details weren't sent with this session."}
    </p>
  );

  if (!files.linked) {
    return (
      <div className={styles.box}>
        {phoneNote}
        <p className={styles.note}>
          {files.noneReceived
            ? "No files reached the server for this session. Either the phone runs an app build that doesn't upload linked files, or the uploads never arrived."
            : "Recorded before files were linked to sessions (April 2026), so its files can't be shown here."}
        </p>
      </div>
    );
  }

  const download = async (key: string) => {
    setError(null);
    try {
      // The signed link is sent as an attachment, so following it saves the file
      // and the panel stays open; no new tab for a popup blocker to stop.
      followDownloadLink(await getFileUrl(key));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const zip = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadZip(sessionId, email, zipName(exercise, timestamp));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.box}>
      {phoneNote}
      {files.expected != null && files.present < files.expected && (
        <p className={styles.note}>
          Expected {files.expected} files (baseline, {files.expected - 2} reps,
          aggregate). Files still queued on the phone can't be seen from here.
        </p>
      )}
      {error && <p className="ui-banner ui-banner--error">{error}</p>}
      <ul className={styles.list}>
        {files.items.map((item, i) => {
          const id = item.artifactId || `${item.type}-${item.repIndex}-${i}`;
          const uploaded = item.status === "uploaded" && !!item.s3Key;
          return (
            <li key={id} className={styles.item}>
              <div className={styles.itemHead}>
                <span className={styles.label}>{label(item)}</span>
                {!uploaded && (
                  <span className="ui-badge ui-badge--warning">
                    {item.status || "not uploaded"}
                  </span>
                )}
                {uploaded && (
                  <span className={styles.actions}>
                    <button
                      type="button"
                      className="ui-btn ui-btn--sm ui-btn--ghost"
                      onClick={() => setOpenGraph(openGraph === id ? null : id)}
                    >
                      {openGraph === id ? "Hide graph" : "Graph"}
                    </button>
                    <button
                      type="button"
                      className="ui-btn ui-btn--sm ui-btn--ghost"
                      onClick={() => download(item.s3Key as string)}
                    >
                      CSV
                    </button>
                  </span>
                )}
              </div>
              {openGraph === id && item.s3Key && (
                <CsvGraph s3Key={item.s3Key} artifactId={id} />
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="ui-btn ui-btn--sm ui-btn--secondary"
        disabled={busy || files.present === 0}
        onClick={zip}
      >
        {busy ? "Preparing…" : "Download all (.zip)"}
      </button>
      <span className={styles.when}>
        Recorded {nyTime(timestamp)} New York time
      </span>
    </div>
  );
}
